import { useState, type FormEvent } from 'react';
import type { UnitSettings } from '@neomaris/protocol';

import { api } from '../api';
import { useAuth, useDemoGuest } from '../auth';
import { NeoMemorySection } from '../components/NeoMemorySection';
import { ScreenLookChoice } from '../components/ScreenLookChoice';
import { UnitSettingsFields } from '../components/UnitSettingsFields';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useUnits } from '../lib/useUnits';

const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

function formatJoined(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function Profile() {
  useDocumentTitle('Profile - Neomaris');
  const auth = useAuth();
  const user = auth.user;
  // A Try it guest (TOM-868) keeps its name and units; it has no address,
  // password or sign-in to manage.
  const guest = useDemoGuest();

  // Display-name editor state.
  const [name, setName] = useState(user?.displayName ?? '');
  const [nameStatus, setNameStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [nameError, setNameError] = useState<string | null>(null);

  // Units editor state. The selects render the in-flight choice (pendingUnits)
  // while a save is running so a pick never visibly snaps back mid-save on a
  // slow link; pendingUnits is cleared when the PATCH settles either way, so
  // after an error the selects revert to the context value (server truth) and
  // "Saved" is only ever driven by the server response, never assumed.
  const units = useUnits();
  const [pendingUnits, setPendingUnits] = useState<UnitSettings | null>(null);
  const [unitsStatus, setUnitsStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [unitsError, setUnitsError] = useState<string | null>(null);

  // Change-password state.
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwStatus, setPwStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pwError, setPwError] = useState<string | null>(null);

  if (user === null) {
    return (
      <section className="dashboard">
        <p className="dashboard-muted">Loading…</p>
      </section>
    );
  }

  async function onSaveName(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setNameStatus('saving');
    setNameError(null);
    try {
      const trimmed = name.trim();
      await api('/v1/me', {
        method: 'PATCH',
        body: { displayName: trimmed === '' ? null : trimmed },
      });
      await auth.refresh();
      setNameStatus('saved');
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      setNameStatus('error');
      setNameError(e.body?.message ?? e.message ?? 'Couldn’t save your name. Please try again.');
    }
  }

  // Save on change — there is no Save button here. Each select is one discrete
  // choice with nothing to validate across fields, so a button would only add a
  // step and a way to walk away with the change unsaved.
  async function onChangeUnits(next: UnitSettings) {
    setPendingUnits(next);
    setUnitsStatus('saving');
    setUnitsError(null);
    try {
      await api('/v1/me', { method: 'PATCH', body: { unitSettings: next } });
      await auth.refresh();
      setUnitsStatus('saved');
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      setUnitsStatus('error');
      setUnitsError(e.body?.message ?? e.message ?? 'Couldn’t save your units. Please try again.');
    } finally {
      setPendingUnits(null);
    }
  }

  async function onChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (next !== confirm) {
      setPwStatus('error');
      setPwError('The two new passwords don’t match.');
      return;
    }
    setPwStatus('saving');
    setPwError(null);
    try {
      await api('/v1/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      setPwStatus('saved');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      setPwStatus('error');
      setPwError(
        e.body?.message ?? e.message ?? 'Couldn’t change your password. Please try again.'
      );
    }
  }

  // The identity header reflects the SAVED name (auth.refresh() updates `user`
  // after a successful save), not the in-progress `name` input — so a failed
  // save never looks persisted. Spread (not charAt(0)) for an astral-safe first
  // glyph; trim so leading whitespace doesn't show in the avatar or heading.
  const savedName = (user.displayName ?? '').trim();
  const avatarChar = [...(savedName !== '' ? savedName : user.email)][0] ?? '';

  const canChangePw =
    pwStatus !== 'saving' &&
    current.length > 0 &&
    next.length >= 8 &&
    confirm.length > 0 &&
    next === confirm;

  return (
    <section className="dashboard profile-shell">
      <header className="dashboard-head">
        <h2>Profile</h2>
        <p className="dashboard-sub">Your Neomaris account.</p>
      </header>

      <div className="profile-identity">
        <div className="profile-avatar" aria-hidden="true">
          {avatarChar}
        </div>
        <div className="profile-identity-body">
          <h3 className={`profile-identity-name${savedName === '' ? ' is-empty' : ''}`}>
            {savedName !== '' ? savedName : 'Add your name'}
          </h3>
          {guest ? (
            <p className="profile-identity-email" data-testid="profile-demo">
              Demo visitor
            </p>
          ) : (
            <p className="profile-identity-email" data-testid="profile-email">
              {user.email}
            </p>
          )}
          {guest ? null : (
            <div className="profile-chips">
              <span className="profile-chip profile-chip--plan">
                {TIER_LABEL[user.tier] ?? user.tier}
              </span>
              {user.authMethods.password ? (
                <span className="profile-chip">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <circle cx="8" cy="15" r="4" />
                    <path
                      d="M10.8 12.2 19 4M16 7l2 2M14 9l2 2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Email &amp; password
                </span>
              ) : null}
              {user.authMethods.google ? (
                <span className="profile-chip">
                  <svg viewBox="0 0 18 18" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                    />
                    <path
                      fill="#34A853"
                      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    />
                    <path
                      fill="#EA4335"
                      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                    />
                  </svg>
                  Google
                </span>
              ) : null}
            </div>
          )}
          {guest ? null : (
            <p className="profile-since">Member since {formatJoined(user.createdAt)}</p>
          )}
        </div>
      </div>

      <div className="profile-section">
        <h3 className="profile-section-heading">Display name</h3>
        <p className="profile-section-help">The name shown across your account. Optional.</p>
        <form onSubmit={onSaveName} data-testid="name-form">
          {nameStatus === 'error' && nameError !== null ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="name-error">
                <li>{nameError}</li>
              </ul>
            </div>
          ) : null}
          {nameStatus === 'saved' ? (
            <p className="profile-confirm" role="status" data-testid="name-saved">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Saved.
            </p>
          ) : null}
          <div className="form-group">
            <label htmlFor="profile-name" className="sr-only">
              Display name
            </label>
            <input
              id="profile-name"
              className="form-input"
              type="text"
              maxLength={100}
              value={name}
              placeholder="e.g. Skipper"
              onChange={(e) => {
                setName(e.target.value);
                if (nameStatus !== 'idle') setNameStatus('idle');
              }}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={nameStatus === 'saving'}>
            {nameStatus === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </form>
      </div>

      <div className="profile-section profile-look" data-testid="screen-look-section">
        <h3 className="profile-section-heading" id="screen-look-heading">
          Screen look
        </h3>
        <p className="profile-section-help">
          System follows this device’s light or dark setting. Night turns the screen red on black
          for the helm at night and stays on this device. Your account remembers the rest, so a new
          browser opens the way you left it.
        </p>
        <ScreenLookChoice labelledBy="screen-look-heading" testIdPrefix="screen-look" />
      </div>

      <div className="profile-section" data-testid="units-section">
        <h3 className="profile-section-heading">Units</h3>
        <p className="profile-section-help">
          How measurements are shown to you, everywhere you’re signed in — Neo included. You can
          also ask Neo to change them.
        </p>
        {unitsStatus === 'error' && unitsError !== null ? (
          <div className="auth-banner auth-banner--error" role="alert">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M12 9v4" strokeLinecap="round" />
              <path
                d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <ul className="form-errors" data-testid="units-error">
              <li>{unitsError}</li>
            </ul>
          </div>
        ) : null}
        <UnitSettingsFields
          value={pendingUnits ?? units}
          disabled={unitsStatus === 'saving'}
          onChange={onChangeUnits}
        />
        <div className="units-status-row" aria-live="polite">
          {unitsStatus === 'saving' ? (
            <p className="units-saving" role="status" data-testid="units-saving">
              Saving…
            </p>
          ) : null}
          {unitsStatus === 'saved' ? (
            <p className="profile-confirm" role="status" data-testid="units-saved">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Saved.
            </p>
          ) : null}
        </div>
      </div>

      <NeoMemorySection />

      {guest ? null : user.authMethods.password ? (
        <div className="profile-section">
          <h3 className="profile-section-heading">Change password</h3>
          <p className="profile-section-help">Update the password you use to sign in.</p>
          <form onSubmit={onChangePassword} data-testid="password-form">
            {pwStatus === 'error' && pwError !== null ? (
              <div className="auth-banner auth-banner--error" role="alert">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M12 9v4" strokeLinecap="round" />
                  <path
                    d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                    strokeLinejoin="round"
                  />
                  <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
                </svg>
                <ul className="form-errors" data-testid="password-error">
                  <li>{pwError}</li>
                </ul>
              </div>
            ) : null}
            {pwStatus === 'saved' ? (
              <p className="profile-confirm" role="status" data-testid="password-saved">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Your password has been changed.
              </p>
            ) : null}
            <div className="form-group">
              <div className="field-password">
                <label htmlFor="current-password">Current password</label>
                <input
                  id="current-password"
                  className="form-input"
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowCurrent((v) => !v)}
                >
                  <span className="sr-only">
                    {showCurrent ? 'Hide current password' : 'Show current password'}
                  </span>
                  {showCurrent ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 3l18 18" strokeLinecap="round" />
                      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                      <path
                        d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="form-group">
              <div className="field-password">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  className="form-input"
                  aria-describedby="new-password-req"
                  type={showNext ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={8}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                />
                <button type="button" className="pw-toggle" onClick={() => setShowNext((v) => !v)}>
                  <span className="sr-only">
                    {showNext ? 'Hide new password' : 'Show new password'}
                  </span>
                  {showNext ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 3l18 18" strokeLinecap="round" />
                      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                      <path
                        d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="pw-hints">
                <span
                  id="new-password-req"
                  className={`pw-hint ${next.length >= 8 ? 'pw-hint--met' : ''}`}
                >
                  {next.length >= 8 ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      aria-hidden="true"
                    >
                      <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                  At least 8 characters
                </span>
              </div>
            </div>
            <div className="form-group">
              <div className="field-password">
                <label htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  className="form-input"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pw-toggle"
                  onClick={() => setShowConfirm((v) => !v)}
                >
                  <span className="sr-only">
                    {showConfirm ? 'Hide password confirmation' : 'Show password confirmation'}
                  </span>
                  {showConfirm ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 3l18 18" strokeLinecap="round" />
                      <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                      <path
                        d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {confirm.length > 0 ? (
                <div className="pw-hints">
                  {next === confirm ? (
                    <span className="pw-hint pw-hint--met">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        aria-hidden="true"
                      >
                        <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Passwords match
                    </span>
                  ) : (
                    <span className="pw-hint pw-hint--pending">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        aria-hidden="true"
                      >
                        <path d="M6 12h12" strokeLinecap="round" />
                      </svg>
                      Passwords don’t match yet
                    </span>
                  )}
                </div>
              ) : null}
            </div>
            <button type="submit" className="btn btn-primary" disabled={!canChangePw}>
              {pwStatus === 'saving' ? 'Changing…' : 'Change password'}
            </button>
          </form>
        </div>
      ) : (
        <div className="profile-section">
          <h3 className="profile-section-heading">Password</h3>
          <p className="profile-note" data-testid="google-only-note">
            <svg viewBox="0 0 18 18" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
              />
            </svg>
            You sign in with Google, so there’s no password to manage here.
          </p>
        </div>
      )}
    </section>
  );
}
